/**
 * Every tunable number in the demand model lives here.
 *
 * These are informed priors, not measurements. Nobody has published the Wolt order
 * distribution for Tel Aviv, and with no earnings log there is nothing to fit against —
 * so the honest position is that version one will be mediocre and the third, after a
 * few weeks of riding with it and adjusting, should be good.
 *
 * When something feels wrong on the street, change it here. Nothing else should need
 * touching.
 */

import type { VenueCategory } from '@tlv/shared';

/**
 * Delivery propensity per category: how likely is this kind of venue to put an order
 * into the queue, relative to a fast-food counter at 1.0.
 *
 * The key judgement is that popularity and delivery volume are different things. A
 * packed wine bar on Rothschild generates almost no delivery orders; an anonymous poke
 * counter with no seating generates a stream of them. Cafés sit low for the same
 * reason — enormous foot traffic, comparatively few deliveries.
 */
export const CATEGORY_WEIGHT: Record<VenueCategory, number> = {
  fast_food: 1.0,
  pizza: 1.0,
  burger: 0.95,
  sushi: 0.95,
  asian: 0.9,
  hummus_mizrahi: 0.85,
  restaurant: 0.7,
  dessert: 0.6,
  juice_health: 0.6,
  convenience: 0.5,
  cafe: 0.45,
  bakery: 0.4,
  fine_dining: 0.2,
  bar: 0.15,
};

/**
 * Demand profiles by hour of day, 0..23, normalised so the daily peak is 1.0.
 *
 * These replace the popular-times data Google does not expose through any API. That
 * turns out to matter less than it sounds: popular times measures bodies inside the
 * room, which is a poor proxy for orders leaving it. Meal timing is what actually
 * drives delivery volume, and meal timing is predictable.
 */
export type DemandProfile = 'breakfast_lunch' | 'lunch_dinner' | 'dinner_late' | 'all_day';

export const DEMAND_PROFILES: Record<DemandProfile, readonly number[]> = {
  // Cafés and bakeries: morning peak, lunch bump, gone by evening.
  breakfast_lunch: [
    0.02, 0.02, 0.01, 0.01, 0.01, 0.02, 0.08, 0.35, 0.6, 0.7, 0.65, 0.6,
    0.8, 0.85, 0.7, 0.5, 0.45, 0.4, 0.35, 0.3, 0.2, 0.12, 0.06, 0.03,
  ],
  // The workhorse curve: lunch rush, then a bigger dinner rush.
  lunch_dinner: [
    0.08, 0.04, 0.02, 0.01, 0.01, 0.01, 0.02, 0.05, 0.1, 0.15, 0.2, 0.4,
    0.85, 1.0, 0.8, 0.5, 0.4, 0.5, 0.7, 0.95, 1.0, 0.9, 0.65, 0.35,
  ],
  // Bars and late kitchens: nothing before evening, alive past midnight.
  dinner_late: [
    0.5, 0.35, 0.2, 0.08, 0.02, 0.02, 0.02, 0.03, 0.05, 0.05, 0.05, 0.1,
    0.2, 0.25, 0.2, 0.15, 0.2, 0.35, 0.55, 0.75, 0.9, 1.0, 0.95, 0.75,
  ],
  // Convenience and dessert: flatter, with an evening lift.
  all_day: [
    0.25, 0.15, 0.1, 0.06, 0.05, 0.06, 0.1, 0.2, 0.35, 0.45, 0.5, 0.55,
    0.7, 0.7, 0.6, 0.55, 0.55, 0.6, 0.7, 0.8, 0.85, 0.8, 0.6, 0.4,
  ],
};

export const CATEGORY_PROFILE: Record<VenueCategory, DemandProfile> = {
  cafe: 'breakfast_lunch',
  bakery: 'breakfast_lunch',
  juice_health: 'breakfast_lunch',

  fast_food: 'lunch_dinner',
  pizza: 'lunch_dinner',
  burger: 'lunch_dinner',
  sushi: 'lunch_dinner',
  asian: 'lunch_dinner',
  hummus_mizrahi: 'lunch_dinner',
  restaurant: 'lunch_dinner',

  bar: 'dinner_late',
  fine_dining: 'dinner_late',

  convenience: 'all_day',
  dessert: 'all_day',
};

/**
 * Overall demand level by weekday, 0 = Sunday.
 *
 * Shaped by the Israeli week rather than a European one: Sunday is a working day,
 * Thursday night is the big night out, and Saturday is bimodal — dead until sundown,
 * then the busiest few hours of the week. The Saturday figure below is the daytime
 * baseline; the מוצ״ש surge is applied separately in SHABBAT, because it is driven by
 * sundown rather than by the clock.
 */
export const DAY_MULTIPLIER: readonly number[] = [
  0.9, // Sunday — back to work
  0.85, // Monday
  0.88, // Tuesday
  0.92, // Wednesday
  1.1, // Thursday — the big one
  1.0, // Friday — heavy lunch, then it empties
  0.95, // Saturday — see SHABBAT below
];

/**
 * Shabbat effects.
 *
 * Two separate things, often confused. Fewer venues are open, which the opening-hours
 * bitmap already handles. But the venues that *are* open see more demand per venue,
 * because the people wanting food have fewer options and are mostly at home. That
 * second effect is what these multipliers capture — and it is why Friday night can be
 * excellent despite half the city being shut.
 */
export const SHABBAT = {
  /** Friday evening through Saturday afternoon: fewer venues, more demand each. */
  duringMultiplier: 1.25,
  /** מוצ״ש — the city comes back on all at once. */
  motzashMultiplier: 1.4,
  /** Motzash window, minutes past midnight on Saturday. */
  motzashStartMin: 20 * 60,
  motzashEndMin: 24 * 60,
} as const;

/**
 * Weather multipliers.
 *
 * Rain does two things at once: more people order in, and fewer couriers want to be
 * out. Both push your earnings the same way, which is why wet evenings are the single
 * most profitable weather in the model. Heat works similarly but weaker.
 *
 * Worth remembering that the model is telling you where the money is, not whether you
 * want to be there. Riding an ebike through a Tel Aviv downpour is your call.
 */
export const WEATHER = {
  heavyRain: 1.6,
  rain: 1.4,
  drizzle: 1.15,
  hot: 1.15, // above HOT_THRESHOLD_C
  pleasant: 1.0,
  hotThresholdC: 32,
} as const;

/**
 * Distance decay: a venue's contribution falls off as exp(-seconds / tau).
 *
 * tau = 180 s means a venue three minutes away counts about 37% as much as one on your
 * doorstep, and one six minutes away about 14%. That is roughly how offer probability
 * behaves — you do get offers from further out, just less often.
 */
export const DECAY_TAU_SECONDS = 180;

/**
 * score = supply^ALPHA * drop^BETA
 *
 * Supply dominates because you cannot be offered an order that no kitchen made. Drop
 * density is a real but secondary correction — it is the difference between an order
 * that keeps you in the middle of everything and one that strands you.
 */
export const ALPHA = 1.0;
export const BETA = 0.35;

/**
 * How much a cell is penalised for sitting at the edge of the dense area.
 *
 * A cell on the boundary may show plenty of nearby kitchens, but its deliveries tend to
 * throw you outward into thin territory, and the ride back is unpaid. At 0.35 a cell
 * with nothing around it keeps 65% of its score.
 */
export const EXILE_PENALTY_STRENGTH = 0.35;

/** k-ring radius used to judge how rich a cell's surroundings are. */
export const EXILE_RING_SIZE = 2;

/**
 * Weight given to the office layer at weekday lunch, and to the residential layer in
 * the evening. Both are floors rather than switches — Sarona is not literally empty at
 * 21:00, it is just much quieter.
 */
export const DROP_WEIGHTS = {
  officeLunchStartMin: 11 * 60,
  officeLunchEndMin: 15 * 60,
  officeBaseline: 0.25,
  residentialBaseline: 0.35,
} as const;

/**
 * Floor applied to the drop term so that a cell with no building data is not zeroed
 * out. OSM building coverage is good in Tel Aviv but not complete, and a missing
 * footprint should cost a cell a little, not everything.
 */
export const DROP_FLOOR = 0.15;
